// Quote Book — time-series quote storage with as-of date resolution
// Each quote id maps to an array of dated {date, value} entries.
// Resolution: for a given as_of_date, pick the appropriate value.
import { QuoteType } from '../types';

export interface DatedValue {
  date: string;   // YYYY-MM-DD
  value: number;
}

export type QuoteResolutionMode = 'previous' | 'exact';

export interface QuoteBookEntry {
  id: string;
  kind: string;          // 'Rate' | 'Spread' | 'Price' | etc.
  quote_type?: QuoteType;
  resolution_mode?: QuoteResolutionMode;
  label?: string;
  currency?: string;
  description?: string;
  series: DatedValue[];  // sorted by date ascending
}

export interface QuoteBookData {
  entries: QuoteBookEntry[];
  resolution_mode: QuoteResolutionMode;
}

export interface QuoteBookExport {
  schema_version: '1';
  as_of_date: string;
  quote_resolution: QuoteResolutionMode;
  quotes: Record<string, DatedValue[]>;
  // Metadata per quote
  quote_meta?: Record<string, { kind?: string; label?: string; currency?: string; description?: string; quote_type?: QuoteType; resolution_mode?: QuoteResolutionMode }>;
}

const QUOTE_BOOK_KEY = 'quantra_quote_book';
const RESOLUTION_MODE_KEY = 'quantra_quote_resolution_mode';

// ============================================================================
// Resolution logic
// ============================================================================

/**
 * Resolve a single quote's value for a given as-of date.
 * "previous": last value with date <= asOfDate
 * "exact": value on exactly asOfDate
 */
export function resolveQuoteValue(
  series: DatedValue[],
  asOfDate: string,
  mode: QuoteResolutionMode = 'previous'
): number | null {
  if (!series || series.length === 0) return null;

  if (mode === 'exact') {
    const match = series.find(s => s.date === asOfDate);
    return match ? match.value : null;
  }

  // "previous" mode: find last entry with date <= asOfDate
  // Series should be sorted ascending
  let result: number | null = null;
  for (const entry of series) {
    if (entry.date <= asOfDate) {
      result = entry.value;
    } else {
      break;
    }
  }
  return result;
}

/**
 * Build a quote snapshot for pricing: resolved {id, value} pairs.
 * Only includes quotes that have a resolved value (skips missing).
 */
export function buildQuoteSnapshot(
  entries: QuoteBookEntry[],
  asOfDate: string,
  mode: QuoteResolutionMode = 'previous'
): { id: string; kind: string; value: number; quote_type?: QuoteType }[] {
  const snapshot: { id: string; kind: string; value: number; quote_type?: QuoteType }[] = [];
  for (const entry of entries) {
    const effectiveMode = entry.resolution_mode || mode;
    const val = resolveQuoteValue(entry.series, asOfDate, effectiveMode);
    if (val !== null) {
      if (entry.quote_type) {
        snapshot.push({ id: entry.id, kind: entry.kind || 'Rate', value: val, quote_type: entry.quote_type });
      } else {
        snapshot.push({ id: entry.id, kind: entry.kind || 'Rate', value: val });
      }
    }
  }
  return snapshot;
}

// ============================================================================
// Persistence
// ============================================================================

function sortSeries(series: DatedValue[]): DatedValue[] {
  return [...series].sort((a, b) => a.date.localeCompare(b.date));
}

export function getQuoteBook(): QuoteBookEntry[] {
  try {
    const raw = localStorage.getItem(QUOTE_BOOK_KEY);
    if (!raw) return [];
    const entries: QuoteBookEntry[] = JSON.parse(raw);
    // Ensure series are sorted
    return entries.map(e => ({ ...e, series: sortSeries(e.series || []) }));
  } catch {
    return [];
  }
}

export function saveQuoteBook(entries: QuoteBookEntry[]): void {
  // Ensure sorted before saving
  const sorted = entries.map(e => ({ ...e, series: sortSeries(e.series || []) }));
  localStorage.setItem(QUOTE_BOOK_KEY, JSON.stringify(sorted));
}

export function getQuoteBookEntry(id: string): QuoteBookEntry | null {
  return getQuoteBook().find(e => e.id === id) || null;
}

export function saveQuoteBookEntry(entry: QuoteBookEntry): void {
  const entries = getQuoteBook();
  const idx = entries.findIndex(e => e.id === entry.id);
  const updated = { ...entry, series: sortSeries(entry.series || []) };
  if (idx >= 0) {
    entries[idx] = updated;
  } else {
    entries.push(updated);
  }
  saveQuoteBook(entries);
}

export function deleteQuoteBookEntry(id: string): void {
  const entries = getQuoteBook().filter(e => e.id !== id);
  saveQuoteBook(entries);
}

/**
 * Add or update a dated value for a quote.
 * If a value already exists for that date, overwrite it.
 */
export function upsertDatedValue(quoteId: string, date: string, value: number): void {
  const entries = getQuoteBook();
  const entry = entries.find(e => e.id === quoteId);
  if (!entry) return;
  
  const existing = entry.series.findIndex(s => s.date === date);
  if (existing >= 0) {
    entry.series[existing].value = value;
  } else {
    entry.series.push({ date, value });
  }
  entry.series = sortSeries(entry.series);
  saveQuoteBook(entries);
}

export function deleteDatedValue(quoteId: string, date: string): void {
  const entries = getQuoteBook();
  const entry = entries.find(e => e.id === quoteId);
  if (!entry) return;
  entry.series = entry.series.filter(s => s.date !== date);
  saveQuoteBook(entries);
}

// ============================================================================
// Resolution mode
// ============================================================================

export function getResolutionMode(): QuoteResolutionMode {
  try {
    const mode = localStorage.getItem(RESOLUTION_MODE_KEY);
    if (mode === 'exact' || mode === 'previous') return mode;
    return 'previous';
  } catch {
    return 'previous';
  }
}

export function setResolutionMode(mode: QuoteResolutionMode): void {
  localStorage.setItem(RESOLUTION_MODE_KEY, mode);
}

// ============================================================================
// Import / Export
// ============================================================================

export function exportQuoteBook(asOfDate: string): QuoteBookExport {
  const entries = getQuoteBook();
  const mode = getResolutionMode();
  
  const quotes: Record<string, DatedValue[]> = {};
  const meta: Record<string, { kind?: string; label?: string; currency?: string; description?: string; quote_type?: QuoteType; resolution_mode?: QuoteResolutionMode }> = {};
  
  for (const entry of entries) {
    quotes[entry.id] = entry.series;
    meta[entry.id] = {
      kind: entry.kind,
      label: entry.label,
      currency: entry.currency,
      description: entry.description,
      quote_type: entry.quote_type,
      resolution_mode: entry.resolution_mode,
    };
  }
  
  return {
    schema_version: '1',
    as_of_date: asOfDate,
    quote_resolution: mode,
    quotes,
    quote_meta: meta,
  };
}

export function importQuoteBook(data: QuoteBookExport, replace: boolean = true): { imported: number; errors: string[] } {
  const errors: string[] = [];
  
  if (data.schema_version !== '1') {
    errors.push(`Unknown schema version: ${data.schema_version}`);
    return { imported: 0, errors };
  }
  
  if (!data.quotes || typeof data.quotes !== 'object') {
    errors.push('Missing or invalid quotes field');
    return { imported: 0, errors };
  }
  
  const existing = replace ? [] : getQuoteBook();
  let imported = 0;
  
  for (const [id, series] of Object.entries(data.quotes)) {
    if (!Array.isArray(series)) {
      errors.push(`Invalid series for quote "${id}"`);
      continue;
    }
    
    // Validate entries
    const validSeries: DatedValue[] = [];
    for (const entry of series) {
      if (typeof entry.date === 'string' && typeof entry.value === 'number' && !isNaN(entry.value)) {
        validSeries.push({ date: entry.date, value: entry.value });
      }
    }
    
    const meta = data.quote_meta?.[id] || {};
    
    const existingIdx = existing.findIndex(e => e.id === id);
    const newEntry: QuoteBookEntry = {
      id,
      kind: meta.kind || 'Rate',
      label: meta.label,
      currency: meta.currency,
      description: meta.description,
      quote_type: meta.quote_type,
      resolution_mode: meta.resolution_mode,
      series: sortSeries(validSeries),
    };
    
    if (existingIdx >= 0) {
      existing[existingIdx] = newEntry;
    } else {
      existing.push(newEntry);
    }
    imported++;
  }
  
  saveQuoteBook(existing);
  
  // Also import resolution mode if present
  if (data.quote_resolution === 'exact' || data.quote_resolution === 'previous') {
    setResolutionMode(data.quote_resolution);
  }
  
  return { imported, errors };
}
